import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../env';
import { routesPath } from '../constants/routes';
import { lastUrlStorage } from '../utilities/helpers';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ─── Request interceptor: attach auth + org headers ─────────────────────────
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  const orgId = localStorage.getItem('hospital-refId');
  if (token && token !== 'null') {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (orgId) {
    config.headers['X-Current-Organization-Id'] = orgId;
  }
  return config;
});

// ─── Response interceptor: store refreshed token + handle errors ─────────────
apiClient.interceptors.response.use(
  (response) => {
    const refreshed = response.headers['x-next-authorization-bearer'];
    if (refreshed) {
      localStorage.setItem('token', refreshed);
    }
    return response;
  },
  (error: AxiosError<{ code?: string; message?: string; extra?: Record<string, any> }>) => {
    const status = error.response?.status;
    const body = error.response?.data;
    const code = body?.code;

    if (code === 'ForceResetPassword' && body?.extra?.resetToken) {
      localStorage.setItem('resetToken', body.extra.resetToken);
      window.location.href = routesPath.resetPassword;
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return new Promise(() => {});
    }

    if (status === 401) {
      localStorage.removeItem('token');
      lastUrlStorage.save();
      toast.error(body?.message ?? 'Not authorized', { autoClose: 5000, closeOnClick: true });
      window.location.href = routesPath.login;
      return Promise.reject(error);
    }

    if (status === 422 && body?.extra?.details) {
      // Don't toast 422 validation errors - let component handle via onUIError
      return Promise.reject(error);
    }

    if (status !== 422) {
      toast.error(body?.message ?? 'Error', { autoClose: 5000, closeOnClick: true });
    }

    return Promise.reject(error);
  },
);

export type ApiError = AxiosError<{ code?: string; message?: string; extra?: Record<string, any> }>;
